'use client';

import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Link, usePathname, useRouter } from '@/i18n/routing';
import {
  IconBell,
  IconBolt,
  IconCheck,
  IconChevronRight,
  IconFileText,
  IconLanguage,
  IconLogin,
  IconLogout,
  IconMail,
  IconMenu2,
  IconMoonStars,
  IconSettings,
  IconShieldLock,
  IconUserPlus,
  IconVideoPlus,
  IconWorldSearch,
  IconX,
} from '@tabler/icons-react';
import { getClient } from '@/lib/supabase/client';
import { fetchCachedApi, CLIENT_CACHE_KEYS } from '@/lib/client-request-cache';
import NotificationPanel from '@/components/social/notifications/NotificationPanel';
import { useSharedNotificationCenter } from '@/components/social/notifications/NotificationCenterProvider';
import { SUPPORTED_LANGUAGES } from '@/i18n/languages';
import { AUTH_REQUIRED_EVENT, requestClientAuth } from '@/lib/auth/client-auth';
import { OPEN_SETTINGS_EVENT, PROFILE_UPDATED_EVENT } from '@/lib/profile-events';
import { resolveProfileAvatarSrc } from '@/lib/profile-avatar-src';
import ProfileAvatar from '@/components/profile/ProfileAvatar';
import AnimatedBrandSymbol from '@/components/AnimatedBrandSymbol';

const AuthModal = dynamic(() => import('@/components/AuthModal'), { ssr: false });
const PricingModal = dynamic(() => import('@/components/PricingModal'), { ssr: false });
const SettingsModal = dynamic(() => import('@/components/SettingsModal'), { ssr: false });
const InviteFriendsModal = dynamic(() => import('@/components/InviteFriendsModal'), { ssr: false });

function isActive(pathname, href) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function formatResetTime(value, t) {
  const target = value ? new Date(value) : null;
  if (!target || Number.isNaN(target.getTime())) return t('sidebar.notAvailable');
  const diff = Math.max(0, target.getTime() - Date.now());
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  return [
    days ? t('sidebar.daysShort', { count: days }) : null,
    t('sidebar.hoursShort', { count: hours }),
    t('sidebar.minutesShort', { count: minutes }),
  ].filter(Boolean).join(' ');
}

function formatDailyResetTime(t) {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return formatResetTime(tomorrow, t);
}

function NavLink({ href, label, Icon, active, onClick }) {
  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`group flex min-h-[58px] w-full flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-[10px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B51B]/60 ${
        active
          ? 'bg-white/[0.10] text-white'
          : 'text-white/50 hover:bg-white/[0.055] hover:text-white/85'
      }`}
    >
      <Icon size={20} stroke={1.75} className={active ? 'text-white' : 'transition-transform group-hover:scale-105'} />
      <span>{label}</span>
    </Link>
  );
}

function ActionButton({ label, Icon, active = false, badge = 0, onClick, showLabel = true }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active || undefined}
      className={`group relative flex min-h-[54px] w-full flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-[10px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B51B]/60 ${
        active ? 'bg-white/[0.09] text-white' : 'text-white/50 hover:bg-white/[0.055] hover:text-white/85'
      }`}
    >
      <Icon size={20} stroke={1.75} className="transition-transform group-hover:scale-105" />
      {showLabel ? <span>{label}</span> : null}
      {!showLabel && !active ? (
        <span className="pointer-events-none absolute left-[calc(100%+8px)] z-[80] whitespace-nowrap rounded-md border border-white/10 bg-[#17191e] px-2 py-1 text-[11px] font-medium text-white/75 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          {label}
        </span>
      ) : null}
      {badge > 0 ? (
        <span className="absolute right-1.5 top-1.5 grid min-h-4 min-w-4 place-items-center rounded-full bg-[#F6B51B] px-1 text-[9px] font-bold leading-none text-[#171006]">
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </button>
  );
}

export default function SocialSidebar({ children }) {
  const t = useTranslations('GateD');
  const tHeader = useTranslations('Header');
  const pathname = usePathname() || '';
  const router = useRouter();
  const locale = useLocale();
  const supabase = useMemo(() => getClient(), []);
  const moreRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [databaseProfile, setDatabaseProfile] = useState(null);
  const [credits, setCredits] = useState(0);
  const [creditDetails, setCreditDetails] = useState({ monthlyCredits: 0, permanentCredits: 0, freeDailyCredits: 0, freeDailyCreditsResetAt: null });
  const [nextMonthlyReset, setNextMonthlyReset] = useState(null);
  const [planName, setPlanName] = useState('Free');
  const [pricingOpen, setPricingOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authRedirectPath, setAuthRedirectPath] = useState(pathname || '/explore');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('account');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const notifications = useSharedNotificationCenter();
  const notificationsOpen = notifications.open;
  const setNotificationsOpen = notifications.setOpen;
  const sidebarAvatarUrl = databaseProfile?.avatarUrl || user?.user_metadata?.avatar_url || null;
  const sidebarAvatarSrc = resolveProfileAvatarSrc({
    id: databaseProfile?.id || user?.id,
    avatarUrl: sidebarAvatarUrl,
  });

  useEffect(() => {
    let active = true;
    const reset = () => {
      if (!active) return;
      setUser(null);
      setDatabaseProfile(null);
      setCredits(0);
      setCreditDetails({ monthlyCredits: 0, permanentCredits: 0, freeDailyCredits: 0, freeDailyCreditsResetAt: null });
      setNextMonthlyReset(null);
      setPlanName('Free');
    };
    const loadUser = async (knownUser = null, force = false) => {
      const resolved = knownUser || (await supabase.auth.getUser()).data.user;
      if (!active) return;
      setUser(resolved || null);
      if (!resolved) {
        reset();
        return;
      }
      try {
        const [creditResponse, subscriptionResponse, profileResponse] = await Promise.all([
          fetchCachedApi('/api/credits', { cacheKey: CLIENT_CACHE_KEYS.credits, ttlMs: 30_000, force }),
          fetchCachedApi('/api/user/subscription', { cacheKey: CLIENT_CACHE_KEYS.subscription, ttlMs: 60_000, force }),
          fetch('/api/user/profile', {
            credentials: 'same-origin',
            cache: 'no-store',
          }).then(async (response) => response.ok ? response.json() : null).catch(() => null),
        ]);
        if (!active) return;
        const creditData = creditResponse.data || {};
        const details = {
          monthlyCredits: Number(creditData.monthlyCredits || 0),
          permanentCredits: Number(creditData.permanentCredits || 0),
          freeDailyCredits: Number(creditData.freeDailyCredits || 0),
          freeDailyCreditsResetAt: creditData.freeDailyCreditsResetAt || null,
        };
        setCreditDetails(details);
        setCredits(details.monthlyCredits + details.permanentCredits + details.freeDailyCredits);
        setPlanName(subscriptionResponse.data?.planName || 'Free');
        setNextMonthlyReset(subscriptionResponse.data?.nextMonthlyReset || null);
        setDatabaseProfile(profileResponse?.user || null);
      } catch {
        // The shell remains useful even when account totals are temporarily unavailable.
      }
    };
    void loadUser();
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') return;
      if (!session?.user) reset();
      else void loadUser(session.user, true);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [supabase]);

  useEffect(() => {
    const openAuth = (event) => {
      event.preventDefault();
      setMoreOpen(false);
      setLanguageOpen(false);
      setContactOpen(false);
      setTermsOpen(false);
      setAuthRedirectPath(event.detail?.redirectPath || pathname || '/explore');
      setAuthOpen(true);
    };
    window.addEventListener(AUTH_REQUIRED_EVENT, openAuth);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, openAuth);
  }, [pathname]);

  useEffect(() => {
    const updateProfile = (event) => setDatabaseProfile(event.detail || null);
    window.addEventListener(PROFILE_UPDATED_EVENT, updateProfile);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, updateProfile);
  }, []);

  useEffect(() => {
    const openRequestedSettings = (event) => {
      if (!user) {
        requestClientAuth({ redirectPath: pathname || '/profile' });
        return;
      }
      setMoreOpen(false);
      setLanguageOpen(false);
      setTermsOpen(false);
      setSettingsTab(event.detail?.tab || 'account');
      setSettingsOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, openRequestedSettings);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, openRequestedSettings);
  }, [pathname, user]);

  useEffect(() => {
    if (!moreOpen) {
      setLanguageOpen(false);
      setContactOpen(false);
      setTermsOpen(false);
      return undefined;
    }
    const close = (event) => {
      if (moreRef.current && !moreRef.current.contains(event.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [moreOpen]);

  const openSettings = (tab) => {
    if (!user) {
      setMoreOpen(false);
      requestClientAuth({ redirectPath: pathname || '/explore' });
      return;
    }
    setMoreOpen(false);
    setLanguageOpen(false);
    setTermsOpen(false);
    setSettingsTab(tab);
    setSettingsOpen(true);
  };

  const openUsageSettings = () => {
    setCreditsOpen(false);
    openSettings('usage');
  };

  const changeLanguage = (nextLocale) => {
    setLanguageOpen(false);
    setMoreOpen(false);
    router.replace(pathname, { locale: nextLocale });
  };

  const currentLanguage = SUPPORTED_LANGUAGES.find((language) => language.code === locale)?.label || 'English';

  const handleCreateNavigation = (event) => {
    setMobileOpen(false);
    if (user) return;
    event.preventDefault();
    requestClientAuth({ redirectPath: `/${locale}/create` });
  };

  const signOut = async () => {
    setMoreOpen(false);
    await supabase.auth.signOut();
    router.push('/');
    router.refresh();
  };

  const sidebarContents = (
    <div className="flex h-full flex-col items-center">
      <Link href="/" aria-label={t('sidebar.botGoHome')} className="grid h-12 w-12 place-items-center rounded-2xl transition hover:bg-white/[0.055]">
        <AnimatedBrandSymbol hoverExpression="blink" size={34} />
      </Link>

      <nav aria-label={t('sidebar.primary')} className="mt-[clamp(3rem,14vh,9rem)] flex w-full flex-col gap-3">
        <NavLink href="/explore" label={t('sidebar.explore')} Icon={IconWorldSearch} active={isActive(pathname, '/explore')} onClick={() => setMobileOpen(false)} />
        <NavLink href="/create" label={t('sidebar.create')} Icon={IconVideoPlus} active={isActive(pathname, '/create')} onClick={handleCreateNavigation} />
      </nav>

      <div className="mt-auto flex w-full flex-col gap-1">
        <div className="relative" onMouseEnter={() => setCreditsOpen(true)} onMouseLeave={() => setCreditsOpen(false)}>
          <button
            type="button"
            onClick={() => setPricingOpen(true)}
            className="group flex min-h-[62px] w-full flex-col items-center justify-center rounded-xl border border-[#F6B51B]/20 bg-[#F6B51B]/[0.055] px-1 text-center transition hover:scale-[1.015] hover:border-[#F6B51B]/38 hover:bg-[#F6B51B]/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B51B]/60"
          >
            <span className="flex items-center gap-1 text-[11px] font-semibold text-[#FFD56A]"><IconBolt size={13} />{user ? credits.toLocaleString(locale) : t('sidebar.credits')}</span>
            <span className="mt-1 text-[8px] font-medium leading-3 text-[#F6B51B]/65">{t('sidebar.upToDiscount')}</span>
          </button>
          {creditsOpen ? (
            <div className="absolute bottom-0 left-full z-[65] w-[calc(22rem+10px)] pl-[10px] text-left">
              <div className="rounded-xl border border-white/10 bg-[#17191e] p-4 shadow-2xl">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-white">{planName}</span>
                  <button type="button" onClick={() => setPricingOpen(true)} className="rounded-[8px] bg-[#F6B51B] px-3 py-1.5 text-xs font-semibold text-[#171006] transition hover:scale-[1.03] hover:bg-[#FFC84A] active:scale-[0.98]">{tHeader('upgrade')}</button>
                </div>
                <div className="mt-3 space-y-2 border-t border-white/10 pt-3 text-xs">
                  <button type="button" onClick={openUsageSettings} className="w-full rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2.5 text-left transition hover:scale-[1.01] hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B51B]/50">
                    <span className="flex justify-between text-white/60"><span>{tHeader('subscriptionCredits')}</span><strong className="text-blue-300">{creditDetails.monthlyCredits}</strong></span>
                    <span className="mt-1.5 flex justify-between text-[10px] text-white/32"><span>{tHeader('nextReset')}</span><span>{formatResetTime(nextMonthlyReset, t)}</span></span>
                  </button>
                  <button type="button" onClick={openUsageSettings} className="w-full rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2.5 text-left transition hover:scale-[1.01] hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B51B]/50">
                    <span className="flex justify-between text-white/60"><span>{tHeader('purchasedCredits')}</span><strong className="text-violet-300">{creditDetails.permanentCredits}</strong></span>
                    <span className="mt-1.5 block text-[10px] text-white/32">{tHeader('neverExpire')}</span>
                  </button>
                  <div className="border-t border-white/10" />
                  <button type="button" onClick={openUsageSettings} className="w-full rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2.5 text-left transition hover:scale-[1.01] hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B51B]/50">
                    <span className="flex justify-between text-white/60"><span>{tHeader('dailyFreeCredits')}</span><strong className="text-emerald-300">{creditDetails.freeDailyCredits}</strong></span>
                    <span className="mt-1.5 flex justify-between text-[10px] text-white/32"><span>{tHeader('nextReset')}</span><span>{formatDailyResetTime(t)}</span></span>
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {user ? (
          <>
            <ActionButton label={t('sidebar.notifications')} showLabel={false} Icon={IconBell} active={notificationsOpen} badge={notifications.unreadCount} onClick={() => setNotificationsOpen(true)} />
            <Link
              href="/profile"
              onClick={() => setMobileOpen(false)}
              aria-label={t('sidebar.profile')}
              className={`flex min-h-[58px] w-full flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 text-[10px] font-medium transition ${isActive(pathname, '/profile') ? 'bg-white/[0.09] text-white' : 'text-white/50 hover:bg-white/[0.055] hover:text-white/85'}`}
            >
              <ProfileAvatar
                src={sidebarAvatarSrc}
                alt=""
                size={28}
                priority
                className="grid h-7 w-7 place-items-center bg-transparent text-[11px] font-bold text-white/75"
                fallback={<span>{(user.email?.[0] || 'U').toUpperCase()}</span>}
              />
            </Link>
          </>
        ) : (
          <ActionButton label={tHeader('signIn')} Icon={IconLogin} onClick={() => setAuthOpen(true)} />
        )}

        <div ref={moreRef} className="relative">
          <ActionButton label={t('sidebar.moreSettings')} showLabel={false} Icon={IconMenu2} active={moreOpen} onClick={() => setMoreOpen((value) => !value)} />
          {moreOpen ? (
            <div className="absolute bottom-0 left-[calc(100%+10px)] z-[66] w-64 rounded-xl border border-white/10 bg-[#17191e] p-1.5 shadow-2xl">
              <button type="button" onClick={() => openSettings('account')} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 transition hover:bg-white/[0.06] hover:text-white"><IconSettings size={17} /><span>{tHeader('settings')}</span></button>
              <div className="relative" onMouseEnter={() => setContactOpen(true)} onMouseLeave={() => setContactOpen(false)}>
                <button type="button" onClick={() => setContactOpen((value) => !value)} aria-expanded={contactOpen} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition ${contactOpen ? 'bg-white/[0.07] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'}`}>
                  <IconMail size={17} /><span>{t('sidebar.contactUs')}</span><IconChevronRight size={15} className="ml-auto" />
                </button>
                {contactOpen ? (
                  <div className="absolute bottom-0 left-full z-[68] w-[calc(13rem+8px)] pl-2">
                    <div className="rounded-xl border border-white/10 bg-[#17191e] p-1.5 shadow-2xl">
                      <a href="mailto:support@botgo.ai" className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-white/70 transition hover:bg-white/[0.06] hover:text-white"><IconMail size={16} />support@botgo.ai</a>
                    </div>
                  </div>
                ) : null}
              </div>
              <div
                className="relative"
                onMouseEnter={() => setLanguageOpen(true)}
                onMouseLeave={() => setLanguageOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setLanguageOpen((value) => !value)}
                  aria-expanded={languageOpen}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition ${languageOpen ? 'bg-white/[0.07] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'}`}
                >
                  <IconLanguage size={17} />
                  <span>{t('sidebar.language')}</span>
                  <span className="ml-auto max-w-24 truncate text-xs text-white/42">{currentLanguage}</span>
                  <IconChevronRight size={15} />
                </button>
                {languageOpen ? (
                  <div className="absolute bottom-0 left-[calc(100%+8px)] z-[67] w-44 rounded-xl border border-white/10 bg-[#17191e] p-1.5 shadow-2xl">
                    {SUPPORTED_LANGUAGES.map((language) => (
                      <button
                        key={language.code}
                        type="button"
                        onClick={() => changeLanguage(language.code)}
                        aria-current={locale === language.code ? 'true' : undefined}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition ${locale === language.code ? 'bg-white/[0.09] text-white' : 'text-white/60 hover:bg-white/[0.06] hover:text-white'}`}
                      >
                        <span className="min-w-0 flex-1 truncate">{language.label}</span>
                        {locale === language.code ? <IconCheck size={15} stroke={2.2} className="shrink-0" /> : null}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="relative" onMouseEnter={() => setTermsOpen(true)} onMouseLeave={() => setTermsOpen(false)}>
                <button type="button" onClick={() => setTermsOpen((value) => !value)} aria-expanded={termsOpen} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition ${termsOpen ? 'bg-white/[0.07] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'}`}>
                  <IconFileText size={17} /><span>{t('sidebar.termsPolicies')}</span><IconChevronRight size={15} className="ml-auto" />
                </button>
                {termsOpen ? (
                  <div className="absolute bottom-0 left-[calc(100%+8px)] z-[67] w-52 rounded-xl border border-white/10 bg-[#17191e] p-1.5 shadow-2xl">
                    <Link href="/terms-of-service" onClick={() => setMoreOpen(false)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-white/70 transition hover:bg-white/[0.06] hover:text-white"><IconFileText size={16} />{t('sidebar.termsOfService')}</Link>
                    <Link href="/privacy-policy" onClick={() => setMoreOpen(false)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-white/70 transition hover:bg-white/[0.06] hover:text-white"><IconShieldLock size={16} />{t('sidebar.privacyPolicy')}</Link>
                  </div>
                ) : null}
              </div>
              {user ? <button type="button" onClick={() => { setMoreOpen(false); setInviteOpen(true); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/70 transition hover:bg-white/[0.06] hover:text-white"><IconUserPlus size={17} /><span>{tHeader('inviteFriends')}</span></button> : null}
              <button type="button" disabled className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/35"><IconMoonStars size={17} /><span>{t('sidebar.theme')}</span></button>
              {user ? <button type="button" onClick={signOut} className="mt-1 flex w-full items-center gap-3 border-t border-white/[0.07] px-3 py-3 text-sm text-red-300/80 transition hover:bg-red-400/[0.06] hover:text-red-200"><IconLogout size={17} /><span>{tHeader('signOut')}</span></button> : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#070a0f] text-white">
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-[78px] bg-[#090c11]/[0.98] px-2 py-3 backdrop-blur-xl lg:block">
        {sidebarContents}
      </aside>

      <div className="sticky top-0 z-50 flex h-14 items-center justify-between bg-[#090c11]/95 px-3 backdrop-blur-xl lg:hidden">
        <Link href="/" aria-label={t('sidebar.botGoHome')}><AnimatedBrandSymbol hoverExpression="blink" size={30} /></Link>
        <button type="button" onClick={() => setMobileOpen((value) => !value)} className="grid h-9 w-9 place-items-center rounded-full bg-white/[0.06] text-white/75" aria-label={t('sidebar.menu')}>
          {mobileOpen ? <IconX size={19} /> : <IconMenu2 size={20} />}
        </button>
      </div>
      {mobileOpen ? (
        <div className="fixed inset-0 z-[48] lg:hidden">
          <button type="button" aria-label={t('sidebar.closeMenu')} className="absolute inset-0 bg-black/65" onClick={() => setMobileOpen(false)} />
          <aside className="absolute bottom-2 left-2 top-2 w-[78px] rounded-2xl border border-white/10 bg-[#090c11] px-2 py-3 shadow-2xl">{sidebarContents}</aside>
        </div>
      ) : null}

      <main className="lg:pl-[78px]">{children}</main>

      <NotificationPanel open={notificationsOpen} onClose={() => setNotificationsOpen(false)} center={notifications} />
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} redirectPath={authRedirectPath} />
      <PricingModal isOpen={pricingOpen} onClose={() => setPricingOpen(false)} />
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} initialTab={settingsTab} />
      <InviteFriendsModal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}
